import apiService from "@/../api/rest";
import * as PayRequestMapper from "../../../../api/mappers/PayRequestMapper";
import * as PayResponseMapper from "../../../../api/mappers/PayResponseMapper";
import { mapFPRequest } from "../../../../api/mappers/FPRequestMapper";
import { InitializePaymentSession, PaymentSession, PaymentSessionArguments } from "@/models/payment/interfaces/payment";
import {
  CREDIT_CARD_PAYMENT_TYPE,
  FREEDOMPAY_SUBPAYMENT_TYPE,
  PAY_TYPE_CREDIT_DEBIT,
  SENTRY_ERRORS
} from "../../../../app-constants";
import { DEFAULT_THEME, addStyleTheme } from "./cssHelper";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";

/**
 * Added in paytype string, needed to tell endpoint if it's CC or AP.
 * @param rootGetters
 * @param commit
 * @param amount
 * @param payType
 */

const THEME = DEFAULT_THEME;

function updateTheme() {
  const rootStyle = document.documentElement.style;
  if (rootStyle) {
    THEME.colors.group2color3 = rootStyle.getPropertyValue("--group-2-color-3");
    THEME.colors.group3color4 = rootStyle.getPropertyValue("--group-3-color-4");
  }
}

export const getPaymentSession = async (
    { rootGetters, commit },
    { amount, payType = PAY_TYPE_CREDIT_DEBIT }: PaymentSessionArguments
): Promise<any> => {
  try {
    const terminalId = rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID;
    const gatewayId = rootGetters["vendor/tokenizedGatewayId"];

    await updateTheme();
    const styles = addStyleTheme(THEME);

    const initPaymentSession: InitializePaymentSession = {
      terminalId,
      amount,
      payType,
      gatewayId,
      styles
    };

    const request = PayRequestMapper.mapInitializePaymentSessionToDTO(initPaymentSession);
    const response = await apiService.payInitializeSession(request);
    const data: PaymentSession = PayResponseMapper.mapInitializeSessionResponseDTOtoPaymentSession(response.data);

    commit("setPaymentSessionToken", data.paymentSessionToken);
    commit("setPaymentSessionKey", data.paymentSessionKey);
    return data;
  } catch (e) {
    errorLoggerAdapter.createIssue(
        SENTRY_ERRORS.ERROR_PAY_SESSION,
        "initPaymentsError",
        "getPaymentSession",
        {
          description: JSON.stringify((e as any).response)
        },
        {}
    );
    return Promise.reject((e as any).response);
  }
};

export const payPayment = async ({ rootGetters }, payload) => {
  let nameOnCard;

  const userData = rootGetters["user/getUserData"];
  const releaseVersion = rootGetters["config/getReleaseVersion"];
  const guestUserData = rootGetters["user/getGuestUserData"];
  const paymentKey = rootGetters["payments/getFPPaymentKey"] || payload?.creditCardData?.tokenCard || "";
  const expDate =  payload?.creditCardData?.expDate || ""
  const tokenCard = payload?.creditCardData?.tokenCard || "";
  const saveCardToken = payload?.saveCardToken || false;
  const terminalId = rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID;
  const sessionKey = rootGetters["payments/getPaymentSessionKey"];
  const amount = rootGetters["cart/getAppliedAmenities"].balance;
  const pos = payload.pos;
  const { payments } = rootGetters["cart/getPaymentsForCheckout"](payload.payment);
  const tax = payments.find(p => p.paymentType === CREDIT_CARD_PAYMENT_TYPE && p.subpaymentType === FREEDOMPAY_SUBPAYMENT_TYPE)
      ?.tax;

  if (payload.nameOnCard) {
    nameOnCard = payload.nameOnCard;
  } else if (userData) {
    nameOnCard = `${userData.profile.firstName} ${userData.profile.lastName}`;
  } else if (guestUserData) {
    nameOnCard = guestUserData.name;
  }

  let expDates = expDate.split("/")
  let expDateMonth,expDateYear
  if (expDates.length == 2) {
    expDateMonth = parseInt(expDates[0],10)
    expDateYear = parseInt(expDates[1],10)
  }

  const requestPayload = mapFPRequest({
    paymentKey,
    terminalId,
    nameOnCard,
    sessionKey,
    amount,
    tax,
    pos,
    releaseVersion,
    expDateMonth,
    expDateYear,
    tokenCard,
    saveCardToken,
    invoiceNumber: payload.invoiceNumber
  });

  const response = await apiService.payPayment(requestPayload);
  // checking for FP Apple Pay, if yes don't parse.
  return pos?.encMode === "applewallet" ? response.data.message : JSON.parse(response.data.message);
};
